import { Box, Checkbox, Typography } from "@mui/material"
import type { ColumnDef, Row } from "@tanstack/react-table"
import dayjs from "dayjs"
import { useCallback, useMemo } from "react"

import StyledChip from "@/components/common/Chip/Chip"
import FileChip from "@/components/upload/components/display/FileChip"
import { SELECT_COLUMN_SIZE } from "@/constants/common"
import { DATE_FORMAT } from "@/constants/formats"
import type { CompanyCertification, SignedUrl } from "@/graphql/codegen/graphql"
import CertificationRowActions from "@/screens/Companies/edit/components/certification/components/tables/CertificationRowActions"
import type { TransformedCertificationTableItem } from "@/screens/Companies/edit/components/certification/types"
import { getCertificationStatus } from "@/screens/Companies/edit/components/certification/utils"
import { CertificationStatusColorMap } from "@/screens/Companies/edit/components/certification/utils/certificationStatusMap"
import { AddressFormat } from "@/types/common"
import { formatLocationAddress } from "@/utils/utils"

export const useCertificationColumns = ({
  fileData = [],
  onEdit,
}: {
  fileData: SignedUrl[]
  onEdit: (certification: CompanyCertification) => void
}): ColumnDef<TransformedCertificationTableItem>[] => {
  const renderCertificationCell = useCallback(
    (companyCertification: CompanyCertification) => {
      const data = fileData.find((el) => el.file?.referenceId === companyCertification.companyCertificationId)
      const certificationStatus = getCertificationStatus(companyCertification.expirationDate!)
      return (
        <Box className='flex gap-2'>
          <StyledChip
            label={dayjs(companyCertification.expirationDate).format(DATE_FORMAT.US)}
            classes={{
              colorDefault: CertificationStatusColorMap.get(certificationStatus),
            }}
            className='text-gray-900'
          />
          {data && <FileChip file={data} showFileName={false} />}
        </Box>
      )
    },
    [fileData]
  )

  const columns = useMemo<ColumnDef<TransformedCertificationTableItem>[]>(
    () => [
      {
        id: "select",
        accessorKey: "select",
        header: "",
        size: SELECT_COLUMN_SIZE,
        cell: ({ row }: { row: Row<TransformedCertificationTableItem> }) => {
          const noCertifications = !row.original?.current && !row.original?.upcoming

          return (
            <Checkbox
              id={row.id}
              checked={row.getIsSelected()}
              onChange={row.getToggleSelectedHandler()}
              disabled={!row.getCanSelect() || noCertifications}
              onClick={(e) => {
                e.stopPropagation()
              }}
            />
          )
        },
        enableSorting: false,
      },
      {
        accessorKey: "location",
        header: "Location",
        cell: ({ row }: { row: Row<TransformedCertificationTableItem> }) => {
          const { location } = row.original

          return (
            <Box className='flex'>
              <Typography variant='body2' color='textSecondary' className='truncate'>
                <span className='text-gray-900'>{location.name}</span> -{" "}
                {formatLocationAddress(location, AddressFormat.AddressOnly)}
              </Typography>
            </Box>
          )
        },
        enableSorting: false,
      },
      {
        accessorKey: "current",
        header: "Current",
        cell: ({ row }: { row: Row<TransformedCertificationTableItem> }) => {
          const current = row.original?.current
          if (!current) return ""

          return renderCertificationCell(current)
        },
        enableSorting: false,
      },
      {
        accessorKey: "upcoming",
        header: "Upcoming",
        cell: ({ row }: { row: Row<TransformedCertificationTableItem> }) => {
          const upcoming = row.original?.upcoming
          if (!upcoming) return ""

          return renderCertificationCell(upcoming)
        },
        enableSorting: false,
      },
      {
        accessorKey: "actions",
        header: "",
        cell: ({ row }) => <CertificationRowActions transformedCertification={row.original} onEdit={onEdit} />,
        size: 24,
        enableSorting: false,
      },
    ],
    [onEdit, renderCertificationCell]
  )

  return columns
}
